import { useCallback, useEffect, useMemo, useState } from "react";
import { useActions } from "app/utils/hooks";
import Fuse from "../../../../vendor/fuse";
import { GroupSelectProps } from "./index.types";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  locations,
  groupChannelObj,
  applyTemplateToChannel,
} from "app/actions/applyTemplate";
import { fetchTeams } from "app/actions/team";
import { RootState } from "typedefs";
import TeamGroupModel from "app/models/TeamGroupModel";
import TeamModel from "app/models/TeamModel";

const initLists: TeamModel[] = [];

const fuseOptions = {
  keys: ["teamGroups.name", "name"],
  includeScore: true,
  includeMatches: true,
  shouldSort: true,
  ignoreLocation: true,
  threshold: 0.4,
};

const TEAM_NAME_KEY = "name";
const CHANNEL_NAME_KEY = "teamGroups.name";

interface FuseMatch {
  key?: string;
  refIndex?: number;
  value?: string;
}

interface FuseResult {
  item: TeamModel;
  matches?: FuseMatch[];
}

export const mapFuseResultsToTeams = (
  fuseResults: FuseResult[],
): TeamModel[] => {
  return fuseResults.map((result) => {
    const matches = result.matches || [];
    const teamNameMatched = matches.some(
      (match) => match.key === TEAM_NAME_KEY,
    );

    if (teamNameMatched) {
      return result.item;
    }

    // Fuse flattens `teamGroups.name` into its own list of values and skips
    // channels without a name, so a match's `refIndex` points into that
    // flattened list rather than into `teamGroups`. The two drift apart as
    // soon as one channel is unnamed, so match on the value instead.
    const matchedChannelNames = new Set(
      matches
        .filter(
          (match) =>
            match.key === CHANNEL_NAME_KEY && typeof match.value === "string",
        )
        .map((match) => match.value),
    );

    const teamGroups = (result.item.teamGroups || []).filter(
      (channel: TeamGroupModel) =>
        typeof channel.name === "string" &&
        matchedChannelNames.has(channel.name),
    );

    // The team matched the search, so never render it with zero channels —
    // that would hide a genuine hit behind missing match metadata.
    if (teamGroups.length === 0) {
      return result.item;
    }

    return { ...result.item, teamGroups };
  });
};

const useChannelSelectModal = ({ onClose, templateId }: GroupSelectProps) => {
  const dispatch = useDispatch();

  const team = useSelector((state: RootState) => state.team, shallowEqual);

  const channelIds = useSelector(
    (state: RootState) => state.applyTemplate.locations,
    shallowEqual,
  );
  const channelsArr = useSelector(
    (state: RootState) => state.applyTemplate.groupChannelObj,
    shallowEqual,
  );

  const applyTemplateActions = useActions({
    applyTemplateToChannel,
  });
  const teamActions = useActions({
    fetchTeams,
  });
  const [searchTerm, setSearch] = useState<string>("");
  const [channels, setChannels] = useState(initLists);
  const [selectedChannelsId, setSelectedChannelsId] =
    useState<string[]>(channelIds);
  const [selectedChannel, setSelectedChannel] =
    useState<TeamGroupModel[]>(channelsArr);

  // The modal is conditionally rendered, so mount = open: fetch here instead
  // of on page load. Channels render nested under teams, hence the include.
  useEffect(() => {
    teamActions.fetchTeams({ "options[include]": ["team_groups"] });
  }, []);

  useEffect(() => {
    if (team.data != null) {
      setChannels(team.data || []);
    }
  }, [team.data]);

  const fuse = useMemo(() => {
    return new Fuse(channels, fuseOptions);
  }, [channels]);

  const results = useMemo(() => {
    if (searchTerm !== "") {
      return mapFuseResultsToTeams(fuse.search(searchTerm));
    } else {
      return channels;
    }
  }, [searchTerm, channels, fuse]);

  const onSearchChannel = useCallback((value: string) => {
    if (value != null && value.length > 0) {
      setSearch(value);
    } else {
      setSearch("");
    }
  }, []);

  const channelSelectHandler = useCallback(
    (value: string, groupChannelObj: TeamGroupModel) => {
      setSelectedChannelsId((prevSelectedChannelsId) => {
        if (prevSelectedChannelsId.includes(value)) {
          return prevSelectedChannelsId.filter((val) => val !== value);
        } else {
          return [...prevSelectedChannelsId, value];
        }
      });
      // Toggle by id, not object identity: the channel objects are rebuilt
      // whenever the team list is refetched, and `selectedChannel` is seeded
      // from Redux, so identity comparison would desync it from
      // `selectedChannelsId` and accumulate duplicates.
      setSelectedChannel((prevSelectedChannel) => {
        if (prevSelectedChannel.some((channel) => channel.id === value)) {
          return prevSelectedChannel.filter((channel) => channel.id !== value);
        } else {
          return [...prevSelectedChannel, groupChannelObj];
        }
      });
    },
    [],
  );

  useEffect(() => {
    dispatch(locations(selectedChannelsId));
    dispatch(groupChannelObj(selectedChannel));
  }, [selectedChannelsId, selectedChannel]);

  const cancelHandler = () => {
    setSelectedChannelsId([]);
    setSelectedChannel([]);
    setSearch("");
    onClose();
  };

  const applyHandler = () => {
    applyTemplateActions.applyTemplateToChannel(selectedChannelsId, templateId);
    setSearch("");
    onClose();
  };

  return {
    channelSelectHandler,
    cancelHandler,
    applyHandler,
    onSearchChannel,
    team,
    searchTerm,
    results,
    selectedChannelsId,
  };
};

export default useChannelSelectModal;
